"use client";

import {useRouter} from "next/navigation";
import {useScriptProposalReview, useScriptProposalNavigation} from "@/contexts/chat/ScriptProposalReviewContext";
import {scriptProposalHref} from "@/services/wingman/mutation-proposal";
import {notifyScriptProposalApplied} from "@/utils/scripts/scriptProposalApplied";
import {UnsavedDataContext} from "@/hooks/useHasUnsavedData";
import {useContext, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import type {AssistantContentPart} from "@/types/chatMessages";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import type {ToolApprovalResponse} from "@/services/wingman";
import ScriptProposalReviewPanel from "./ScriptProposalReviewPanel";
import ToolApprovalCard from "./ToolApprovalCard";
import {combinedApprovalStatus, resolveToolApprovalStatus} from "./toolApprovalStatus";

export type ApprovalPart = Extract<AssistantContentPart, {type: "functionCall"}>;

/** The chat owns live approval state; the shared panel only hosts its review UI. */
export default function ApprovalReview({parts, conversationId, messageId, approvalMessageIds, activeMessageId, disabled, onRespond}: {
   parts: ApprovalPart[];
   conversationId?: string;
   messageId?: string;
   approvalMessageIds?: Record<string, string>;
   activeMessageId?: string;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<void>;
}) {
   const {t} = useTranslation("ask");
   const router = useRouter();
   const pageReview = useScriptProposalReview();
   const opened = useRef(false);
   const didRespond = useRef(false);
   const shouldFinishReview = useRef(false);
   const {hasUnsavedData, checkBeforeAction} = useContext(UnsavedDataContext);
   const {activeArtifact, openArtifact, closeArtifact, approvalPanelHost, autoOpenedIds} = useArtifactPanel();
   const openedProposals = useScriptProposalNavigation() ?? autoOpenedIds;
   const busy = useRef(false);
   const [pending, setPending] = useState(false);
   const id = `approval:${parts[0].data.approval!.id}`;
   const isOpen = activeArtifact?.kind === "approval" && activeArtifact.id === id;
   const title = parts.length === 1
      ? parts[0].data.approval!.proposal?.summary ?? parts[0].data.functionName
      : t("toolApproval.proposals", {count: parts.length});
   const isActive = (part: ApprovalPart) => !!messageId &&
      (approvalMessageIds?.[part.data.approval!.id] ?? messageId) === activeMessageId;
   const statuses = parts.map(part => resolveToolApprovalStatus(false, part.data.approval!.approved,
      part.isCompleted, isActive(part), part.result));
   const status = combinedApprovalStatus(statuses, pending);

   const scriptPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return !!proposal && !!scriptProposalHref(proposal, conversationId);
   });
   const preview = scriptPart?.data.approval?.proposal;
   const href = preview ? scriptProposalHref(preview, conversationId) : undefined;
   const matchingPart = parts.find(part => {
      const proposal = part.data.approval?.proposal;
      return !!pageReview && !!proposal && proposal.proposalId === pageReview.proposalId &&
         proposal.targetId.toLowerCase() === pageReview.scriptId.toLowerCase() &&
         proposal.after.direction === pageReview.direction;
   });
   const matchingStatus = matchingPart ? statuses[parts.indexOf(matchingPart)] : undefined;
   const setHasReview = pageReview?.setHasReview;
   useEffect(() => {
      if (!matchingPart || !setHasReview) return;
      openedProposals.add(id);
      setHasReview(true);
      return () => setHasReview(false);
   }, [matchingPart, setHasReview, openedProposals, id]);
   useEffect(() => {
      if (pageReview || opened.current || openedProposals.has(id) || hasUnsavedData || disabled || !href || !scriptPart || !isActive(scriptPart) ||
         !preview || Date.parse(preview.expiresAt) <= Date.now() ||
         scriptPart.isCompleted || scriptPart.data.approval?.approved !== undefined) return;
      openedProposals.add(id);
      opened.current = true;
      router.push(href);
   }, [pageReview, openedProposals, id, preview, hasUnsavedData, disabled, href, scriptPart, activeMessageId, messageId, approvalMessageIds, router]);
   // The script editor stays mounted through a decision, so tell it what landed. An
   // indeterminate outcome may have changed the script too; reconciling shows the truth.
   const notified = useRef(new Set<string>());
   useEffect(() => {
      if (!didRespond.current) return;
      parts.forEach((part, index) => {
         const {id: approvalId, proposal} = part.data.approval!;
         if ((statuses[index] !== "completed" && statuses[index] !== "indeterminate") || !proposal || !scriptProposalHref(proposal, conversationId) ||
            notified.current.has(approvalId)) return;
         notified.current.add(approvalId);
         notifyScriptProposalApplied(proposal.targetId);
      });
   });
   const finish = pageReview?.finish;
   useEffect(() => {
      if (finish && shouldFinishReview.current && !disabled && (matchingStatus === "completed" || matchingStatus === "denied")) {
         // Consume this decision's automatic close so manually reopening stays open.
         shouldFinishReview.current = false;
         finish();
      }
   }, [finish, disabled, matchingStatus]);

   // A route change must not leave an orphaned proposal in the shared panel.
   useEffect(() => () => closeArtifact(id), [closeArtifact, id]);

   const respond = async (decision: ToolApprovalResponse) => {
      if (busy.current || disabled || !onRespond) return;
      didRespond.current = true;
      shouldFinishReview.current = true;
      busy.current = true;
      setPending(true);
      // The deciding surface reports a failed submission; the proposal stays open to retry.
      try {
         await onRespond(decision);
      } finally {
         busy.current = false;
         setPending(false);
      }
   };

   return <>
      <section aria-label={t("toolApproval.title")} className="my-3 rounded-xl border border-300 bg-panel">
         <button type="button" aria-expanded={!!matchingPart || isOpen}
            onClick={async () => {
               if (href) { if (await checkBeforeAction()) router.push(href); }
               else openArtifact({kind: "approval", id, title});
            }}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors hover:bg-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-300 bg-100 text-600">
               <Icon icon={status === "completed" ? "check" : "file-lines"} className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
               <span className="block break-words text-sm font-semibold text-800">{title}</span>
               <output className="mt-1 block text-xs text-500">{t(`toolApproval.${status}`)}</output>
            </span>
            <span className="shrink-0 text-xs font-medium text-700">{t("toolApproval.review")}</span>
            <Icon icon="chevron-right" className="h-3 w-3 shrink-0 text-400" />
         </button>
      </section>
      {matchingPart && pageReview?.host && createPortal(
         <ScriptProposalReviewPanel call={matchingPart.data} completed={matchingPart.isCompleted} result={matchingPart.result}
            active={isActive(matchingPart)} disabled={disabled || pending} onRespond={onRespond ? respond : undefined}
            onClose={pageReview.finish} />,
         pageReview.host)}
      {isOpen && approvalPanelHost && createPortal(
         <div className="h-full min-h-0 overflow-y-auto overscroll-contain px-4 py-1">
            {parts.map(part => <ToolApprovalCard key={part.data.approval!.id}
               call={part.data} completed={part.isCompleted} result={part.result} active={isActive(part)}
               disabled={disabled || pending} onRespond={onRespond ? respond : undefined} />)}
         </div>, approvalPanelHost)}
   </>;
}
